import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

/**
 * Success content for the payment success modal (matches wallet/PaymentForm design).
 * Orange check icon, title, message, "How to Install eSIM" → activateEsim, "Go Back to Home".
 */
export const PaymentSuccessModalContent = ({ onInstallEsim, onGoHome }) => {
  const { t } = useTranslation();
  const getPublicUrl = (path) => (typeof window !== 'undefined' && window.getPublicUrl ? window.getPublicUrl(path) : path);
  return (
    <div className="paymentsuccess">
      <div className="modal-content border-0">
        <div className="modal-body">
          <div className="success-img d-flex justify-content-center">
            <img src={getPublicUrl('images/check-circle.svg')} className="img-fluid" alt="Success" />
          </div>
          <div className="row">
            <div className="col-sm-12 text-center">
              <h3>{t('paymentForm.success.title')}</h3>
              <p className="mb-0">{t('paymentForm.success.message')}</p>
            </div>
          </div>
        </div>
        <div className="modal-footer border-0 pt-0">
          <button
            type="button"
            className="btn view-all get-sim"
            onClick={onInstallEsim}
          >
            {t('paymentForm.success.howToInstall')}
          </button>
          <button
            type="button"
            className="view-all get-sim Continue"
            onClick={onGoHome}
          >
            {t('paymentForm.success.goBackHome')}
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentSuccess = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fromUrl = searchParams.get('payment_intent');
    const fromState = location.state?.paymentIntentId;
    const paymentIntentId = fromUrl || fromState;
    const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');

    if (paymentIntentId) {
      setPaymentDetails({
        paymentIntentId,
        paymentIntentClientSecret,
        status: 'succeeded',
      });
    }

    setIsLoading(false);
    toast.success(t('paymentSuccess.toastMessage'));
  }, [searchParams, location.state]);

  if (isLoading) {
    return (
      <section className="destinations checkout">
        <div className="container">
          <div className="row">
            <div className="col-sm-12">
              <div className="checkout-left text-center">
                <div className="logo">
                  <Link to="/">
                    <img src="images/logo.svg" alt="Logo" />
                  </Link>
                </div>
                <div className="loading">
                  <h3>{t('paymentSuccess.processing')}</h3>
                  <div className="spinner"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="destinations checkout">
      <div className="container">
        <div className="row">
          <div className="col-sm-12">
            <div className="checkout-left text-center">
              <div className="logo">
                <Link to="/">
                  <img src="images/logo.svg" alt="Logo" />
                </Link>
              </div>

              <div className="payment-success">
                <div className="success-icon">
                  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="40" cy="40" r="40" fill="#4CAF50"/>
                    <path d="M25 40L35 50L55 30" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>

                <h2>{t('paymentSuccess.title')}</h2>
                <p>{t('paymentSuccess.message')}</p>

                {paymentDetails && (
                  <div className="payment-details">
                    <p><strong>{t('paymentSuccess.paymentId')}:</strong> {paymentDetails.paymentIntentId}</p>
                    <p><strong>{t('paymentSuccess.status')}:</strong> {paymentDetails.status}</p>
                  </div>
                )}

                <div className="next-steps">
                  <h4>{t('paymentSuccess.whatsNext')}</h4>
                  <ul>
                    <li>{t('paymentSuccess.step1')}</li>
                    <li>{t('paymentSuccess.step2')}</li>
                    <li>{t('paymentSuccess.step3')}</li>
                  </ul>
                </div>

                <div className="action-buttons">
                  <Link to="/" className="view-all get-sim">
                    {t('paymentSuccess.continueShopping')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PaymentSuccess;
